'use client'

import { getCurrentGitTag } from '@/utils/gitVersion'
import { usePathname, useSearchParams } from 'next/navigation'
import { memo, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import useAuth from '@/hooks/useAuth'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import { selectStoreOrder } from '@/redux/order/selector'
import { selectStoreState } from '@/redux/stores/selector'

const setCustomAttributes = (attributes: Record<string, unknown>): void => {
  if (typeof window === 'undefined' || !window.NREUM) return

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined) return

    window.NREUM.setCustomAttribute(key, value ?? null)
  })
}

const addPageAction = (
  name: string,
  attributes?: Record<string, unknown>
): void => {
  if (typeof window === 'undefined' || !window.NREUM) return

  window.NREUM.addPageAction(name, attributes)
}

export const TelemetryManager = memo(function TelemetryManager() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const detailMenuSite = useSelector(selectDetailMenuSite)
  const storeState = useSelector(selectStoreState)
  const storeOrderState = useSelector(selectStoreOrder)
  const { data: userResponse, status: authStatus } = useAuth()

  const search = useMemo(() => searchParams?.toString() ?? '', [searchParams])

  const fullPath = useMemo(() => {
    if (!pathname) return null
    if (typeof window === 'undefined') return null

    return search
      ? `${window.location.origin}${pathname}?${search}`
      : `${window.location.origin}${pathname}`
  }, [pathname, search])

  const menuId = detailMenuSite?.menu?.id ?? detailMenuSite?.menuId
  const menuName = detailMenuSite?.menu?.name ?? detailMenuSite?.menuName
  const menuCountry = detailMenuSite?.menuCountry

  const storeOrder = storeOrderState?.data
  const storeOrderStatus = storeOrderState?.status
  const storeId = storeOrder?.storeId
  const storeName = storeOrder?.storeName
  const orderId = storeOrder?.id
  const orderStatus = storeOrder?.status
  const isPickupOrder = storeOrder?.isPickup
  const orderMethod =
    typeof isPickupOrder === 'boolean'
      ? isPickupOrder
        ? 'pickup'
        : 'delivery'
      : undefined

  const storeEntities = storeState?.data?.entities as
    | Record<string, Oms.Store>
    | undefined

  const storeDetails =
    storeId && storeEntities ? storeEntities[storeId] : undefined
  const storeSupportsPickup = storeDetails?.enablePickup
  const storeSupportsDelivery = storeDetails?.enableDelivery

  const userPayload = userResponse?.data?.payload
  const userId = userPayload?.ocaId ?? userPayload?.id ?? null
  const userEmail = userPayload?.email ?? null
  const userPhone = userPayload?.phone ?? null
  const userCountry = userPayload?.country ?? null
  const isUserLoggedIn = Boolean(userPayload?.id || userPayload?.ocaId)

  useEffect(() => {
    setCustomAttributes({
      'app.version': getCurrentGitTag(),
    })
  }, [])

  useEffect(() => {
    if (!pathname) return

    setCustomAttributes({
      'url.pathname': pathname,
      'url.search': search,
      'url.fullPath': fullPath,
    })
  }, [pathname, search, fullPath])

  useEffect(() => {
    if (!pathname) return

    addPageAction('page_view', {
      path: pathname,
      fullPath,
      menuId,
    })
  }, [pathname, fullPath, menuId])

  useEffect(() => {
    if (!menuId && !menuName && !menuCountry) return
    setCustomAttributes({
      'menu.id': menuId ?? null,
      'menu.name': menuName ?? null,
      'menu.country': menuCountry ?? null,
    })
  }, [menuId, menuName, menuCountry])

  useEffect(() => {
    if (!storeOrderStatus || storeOrderStatus === 'none') return

    setCustomAttributes({
      'order.id': orderId ?? null,
      'order.status': orderStatus ?? null,
      'order.method': orderMethod ?? null,
      'store.id': storeId ?? null,
      'store.name': storeName ?? null,
      'store.supportsPickup': storeSupportsPickup,
      'store.supportsDelivery': storeSupportsDelivery,
    })
  }, [storeOrderStatus, orderId, orderStatus, orderMethod, isPickupOrder, storeId, storeName, storeSupportsPickup, storeSupportsDelivery])

  useEffect(() => {
    if (!authStatus || authStatus === 'uninitialized') return
    if (!userResponse || userResponse?.data?.errorCode) return

    setCustomAttributes({
      'user.isLoggedIn': isUserLoggedIn,
      'user.id': userId,
      'user.email': userEmail,
      'user.phone': userPhone,
      'user.country': userCountry,
    })
  }, [authStatus, userResponse, isUserLoggedIn, userId, userEmail, userPhone, userCountry])

  return null
})
